import { User } from '@/api/models';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Pagination } from '@/components/ui/Pagination';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { queryKeys } from '@/lib/queryKeys';
import { mdiAccount, mdiAccountGroup, mdiPlus, mdiRefresh } from '@mdi/js';
import * as React from 'react';
import { UsersFilters } from './UsersFilters';
import { UsersTable } from './UsersTable';

export interface UsersListProps {
  onRowClick: (user: User) => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  onCreateUser: () => void;
  isDeleting: boolean;
}

export const UsersList: React.FC<UsersListProps> = ({
  onRowClick,
  onEditUser,
  onDeleteUser,
  onCreateUser,
  isDeleting,
}) => {
  const { state, list, actions, isLoading } = usePaginatedList<
    User,
    { role: string; enabled: string }
  >({
    queryKey: queryKeys.ADMIN_USERS,
    endpoint: '/admin/users',
    initialFilters: { role: '', enabled: '' },
    buildQueryParams: ({
      page,
      limit,
      search,
      sortBy,
      sortOrder,
      filters,
    }) => ({
      page,
      limit,
      search: search || undefined,
      role: filters.role || undefined,
      enabled: filters.enabled ? filters.enabled === 'true' : undefined,
      sortBy,
      sortOrder,
    }),
  });

  return (
    <div className='space-y-6'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-grey-900 flex items-center'>
            <Icon path={mdiAccount} size={32} className='mr-3' />
            Users
          </h1>
          <p className='text-grey-600 mt-2'>
            Manage users and their permissions
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-3'>
          <div className='text-sm text-grey-600'>
            <Icon path={mdiAccountGroup} size={16} className='mr-1 inline' />
            {list.total} total users
          </div>
          <Button
            icon={mdiRefresh}
            color='light'
            title='Refresh'
            onClick={actions.refetch}
            loading={isLoading}
          />
          <Button
            label='Add User'
            icon={mdiPlus}
            color='primary'
            onClick={onCreateUser}
          />
        </div>
      </div>

      <UsersFilters
        searchTerm={state.search}
        selectedRole={state.filters.role}
        selectedEnabled={state.filters.enabled}
        onSearchChange={actions.setSearch}
        onRoleChange={value => actions.setFilter('role', value)}
        onEnabledChange={value => actions.setFilter('enabled', value)}
        onClearFilters={actions.clearFilters}
      />

      <UsersTable
        users={list.items}
        isLoading={isLoading}
        sortBy={state.sortBy}
        sortOrder={state.sortOrder}
        onSort={actions.setSort}
        onRowClick={onRowClick}
        onEditUser={onEditUser}
        onDeleteUser={onDeleteUser}
        isDeleting={isDeleting}
      />

      {list.totalPages > 1 && (
        <Pagination
          currentPage={list.currentPage}
          totalPages={list.totalPages}
          onPageChange={actions.setPage}
          totalItems={list.total}
          itemsPerPage={list.limit}
        />
      )}
    </div>
  );
};
